import { ReactNode, useEffect, useRef } from "react";
import CloseIcon from "frontend/ui-components/design-system/icons/close-icon";
import StyledButton from "frontend/ui-components/styled-button";
import styles from "./onboarding-panel.module.css";
import tracking from "frontend/tracking";
import classNames from "classnames";
import { useOnClickOutside } from "usehooks-ts";
import { stageRefAtom } from "state-atoms";
import { useAtomValue } from "jotai";

const BUTTON_STYLE = {
  border: "none",
  borderRadius: "5px",
  color: "white",
  fontFamily: "Poppins",
  fontSize: "12px",
  fontWeight: "400",
  letterSpacing: "0.13px",
  height: "26px",
  width: "71px",
  alignSelf: "flex-end",
};
export interface OnboardingPanelProps {
  name: string;
  children: ReactNode;
  title?: string;
  description?: string;
  titleIcon?: ReactNode;
  ctaText: string;
  onCtaClick: () => void;
  onClose: () => void;
  onDismiss?: () => void;
  centered?: boolean;
}

export default function OnboardingPanel({
  name,
  children,
  title,
  description,
  titleIcon,
  ctaText,
  onCtaClick,
  onClose,
  onDismiss,
  centered = true,
}: OnboardingPanelProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const stageRef = useAtomValue(stageRefAtom);

  useEffect(() => {
    tracking.trackAnalyticsEvent(`${name}_viewed`);
  }, [name]);

  const handleClose = () => {
    tracking.trackAnalyticsEvent(`${name}_closed`);
    onClose();
  };

  const handleCtaClick = () => {
    tracking.trackAnalyticsEvent(`${name}_cta_clicked`);
    onCtaClick();
  };

  const handleClickOutside = (event: MouseEvent | TouchEvent) => {
    if (!onDismiss) return;

    const target = event.target as HTMLElement | null;

    const isActualCanvasClick = target?.tagName === "CANVAS" && stageRef?.current?.container().contains(target);

    if (!isActualCanvasClick) {
      tracking.trackAnalyticsEvent(`${name}_dismissed`);
      onDismiss();
    }
  };

  useOnClickOutside(containerRef, handleClickOutside);

  return (
    <div
      ref={containerRef}
      className={classNames(styles.container, {
        [styles.fixedPosition]: centered,
      })}
    >
      <CloseIcon color="#848199" width={10} height={10} className={styles.closeButton} onClick={handleClose} />
      <div className={styles.contentSection}>{children}</div>

      <div className={styles.infoSection}>
        <div className={styles.infoSectionContent}>
          {title ? (
            <h2 className={styles.title}>
              {titleIcon}
              {title}
            </h2>
          ) : null}
          {description ? <p className={styles.description}>{description}</p> : null}
        </div>

        <StyledButton title={ctaText} onClick={handleCtaClick} customStyle={BUTTON_STYLE} />
      </div>
    </div>
  );
}
